import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import channelService, { Channel } from '@/services/channelService';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { logError } from '@/utils/errorHandler';

interface ChannelFilterProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

const ChannelFilter: React.FC<ChannelFilterProps> = ({ value, onChange }) => {
  // Get translations
  const i18n = useReportspageTranslations();

  const {
    data: channels,
    isLoading,
    error,
  } = useQuery<Channel[]>({
    queryKey: ['channels', 'analytics'],
    queryFn: channelService.getChannels,
  });

  // Map of channel codes to their descriptions for display purposes
  const channelNameMap = React.useMemo(() => {
    if (!channels) return {};

    return channels.reduce(
      (acc: Record<string, string>, channel: Channel) => {
        acc[channel.code] = channel.name || channel.label || channel.description || channel.code;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [channels]);

  if (isLoading) {
    return <Skeleton className="h-10 w-[200px]" />;
  }

  if (error) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      component: 'ChannelFilter',
      action: 'loadChannels',
      metadata: {
        context: 'channel_filter_loading_failure',
      },
    });
  }

  return (
    <div className="flex flex-col space-y-1">
      <label htmlFor="channel-filter" className="text-sm font-medium">
        {i18n.filters.labels.channel}
      </label>
      <Select value={value ?? 'all'} onValueChange={onChange}>
        <SelectTrigger id="channel-filter" className="w-[200px]">
          <SelectValue placeholder={i18n.filters.placeholders.allChannels}>
            {value && value !== 'all'
              ? channelNameMap[value] || i18n.filters.placeholders.allChannels
              : i18n.filters.placeholders.allChannels}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{i18n.filters.placeholders.allChannels}</SelectItem>
          {channels && channels.length > 0 ? (
            channels.map((channel: Channel) => (
              <SelectItem key={channel.code} value={channel.code}>
                {channel.name || channel.label || channel.description || channel.code}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="no-channels" disabled>
              {i18n.filters.options.noChannels}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ChannelFilter;
